import { X } from "lucide-react";
import { type KeyboardEvent, useState } from "react";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useGoogleContactsSearch } from "@/hooks/use-google-contacts";
import { cn } from "@/lib/utils";

interface GoogleRecipientInputProps {
  value: string[];
  onChange: (emails: string[]) => void;
  disabled?: boolean;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value);
}

/**
 * The recipient input for sending a Google Calendar invite (EPIC-013): one
 * combined control — typed text plus Google Contacts autocomplete — that
 * represents multiple confirmed recipients as removable chips. A typed entry
 * that doesn't match a suggestion is taken literally once it passes a basic
 * email-format check.
 *
 * New component, not built on AsyncCombobox: that's single-select and closes
 * on pick, whereas this is a different interaction model (multi-select
 * chips). Owns its own debounced search + fetch (unlike the presentational-
 * AsyncCombobox-plus-wrapper split used for single-value pickers) since
 * there's exactly one consumer today — see the EPIC-013 build spec.
 */
export function GoogleRecipientInput({ value, onChange, disabled }: GoogleRecipientInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const debouncedSearch = useDebouncedValue(inputValue, 300);
  const { data, isFetching } = useGoogleContactsSearch(debouncedSearch);
  const suggestions = (data ?? []).filter(
    (contact) => !value.some((email) => email.toLowerCase() === contact.email.toLowerCase()),
  );
  const showSuggestions = inputValue.trim().length >= 2;

  function addEmail(email: string) {
    const normalized = email.trim();
    const alreadyAdded = value.some(
      (existing) => existing.toLowerCase() === normalized.toLowerCase(),
    );
    if (!alreadyAdded) onChange([...value, normalized]);
    setInputValue("");
    setError(null);
    setHighlighted(0);
  }

  function removeEmail(email: string) {
    onChange(value.filter((existing) => existing !== email));
  }

  function commitTyped() {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) {
      setError("Enter a valid email address");
      return;
    }
    addEmail(trimmed);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlighted((i) => Math.min(suggestions.length - 1, i + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlighted((i) => Math.max(0, i - 1));
    } else if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      const highlightedContact = showSuggestions ? suggestions[highlighted] : undefined;
      if (highlightedContact) {
        addEmail(highlightedContact.email);
      } else {
        commitTyped();
      }
    } else if (event.key === "Backspace" && inputValue === "" && value.length > 0) {
      const last = value[value.length - 1];
      if (last !== undefined) removeEmail(last);
    }
  }

  return (
    <div className="space-y-1">
      <div
        className={cn(
          "flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border border-input bg-transparent px-2 py-1.5 text-sm shadow-xs",
          error && "border-destructive",
        )}
      >
        {value.map((email) => (
          <span
            key={email}
            className="flex items-center gap-1 rounded-sm bg-muted px-2 py-0.5 text-xs"
          >
            {email}
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => removeEmail(email)}
              disabled={disabled}
              aria-label={`Remove ${email}`}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          value={inputValue}
          onChange={(event) => {
            setInputValue(event.target.value);
            setError(null);
            setHighlighted(0);
          }}
          onKeyDown={handleKeyDown}
          onBlur={commitTyped}
          disabled={disabled}
          placeholder={value.length === 0 ? "Type a name or email…" : ""}
          className="min-w-[120px] flex-1 bg-transparent py-0.5 text-sm outline-none placeholder:text-muted-foreground"
        />
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      {showSuggestions && (
        <ul className="max-h-48 overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
          {isFetching && suggestions.length === 0 ? (
            <li className="px-2 py-2 text-center text-xs text-muted-foreground">Searching…</li>
          ) : suggestions.length === 0 ? (
            <li className="px-2 py-2 text-center text-xs text-muted-foreground">
              No Google contacts found — press Enter to add as typed.
            </li>
          ) : (
            suggestions.map((contact, index) => (
              <li key={contact.email}>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => addEmail(contact.email)}
                  onMouseEnter={() => setHighlighted(index)}
                  className={cn(
                    "flex w-full flex-col items-start rounded-sm px-2 py-1.5 text-left text-sm",
                    index === highlighted && "bg-accent text-accent-foreground",
                  )}
                >
                  <span>{contact.name}</span>
                  <span className="text-xs text-muted-foreground">{contact.email}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
